/**
 * Confirmation Composable
 * Provides reusable confirmation dialog functionality
 */

import { ref, reactive, readonly } from 'vue'
import { MESSAGES } from '@/constants/common'

export function useConfirmation(options = {}) {
  const {
    defaultTitle = 'Confirm Action',
    defaultDescription = 'Are you sure you want to proceed?',
    defaultConfirmText = 'Confirm',
    defaultCancelText = 'Cancel',
    defaultIconType = 'warning',
    defaultConfirmVariant = 'danger',
    defaultCancelVariant = 'secondary'
  } = options

  // State
  const isOpen = ref(false)
  const loading = ref(false)
  const config = reactive({
    title: defaultTitle,
    description: defaultDescription,
    details: [],
    warningText: null,
    confirmText: defaultConfirmText,
    cancelText: defaultCancelText,
    confirmVariant: defaultConfirmVariant,
    cancelVariant: defaultCancelVariant,
    iconType: defaultIconType,
    showIcon: true,
    showCancel: true,
    size: 'md',
    closable: true,
    requireInput: false,
    inputLabel: 'Type to confirm',
    inputPlaceholder: 'Enter confirmation text',
    inputType: 'text',
    inputHelper: null,
    expectedInput: null
  })

  // Callbacks
  let confirmCallback = null
  let cancelCallback = null

  // Methods
  const show = (customConfig = {}) => {
    return new Promise((resolve, reject) => {
      // Reset config to defaults first
      config.title = defaultTitle
      config.description = defaultDescription
      config.details = []
      config.warningText = null
      config.confirmText = defaultConfirmText
      config.cancelText = defaultCancelText
      config.confirmVariant = defaultConfirmVariant
      config.cancelVariant = defaultCancelVariant
      config.iconType = defaultIconType
      config.showIcon = true
      config.showCancel = true
      config.size = 'md'
      config.closable = true
      config.requireInput = false
      config.inputLabel = 'Type to confirm'
      config.inputPlaceholder = 'Enter confirmation text'
      config.inputType = 'text'
      config.inputHelper = null
      config.expectedInput = null
      
      // Merge custom config with defaults
      Object.assign(config, customConfig)
      
      // Set up callbacks
      confirmCallback = (data) => {
        resolve({ confirmed: true, data })
        isOpen.value = false
        loading.value = false
      }
      
      cancelCallback = () => {
        resolve({ confirmed: false })
        isOpen.value = false
        loading.value = false
      }
      
      // Show dialog
      isOpen.value = true
    })
  }

  const confirm = (title, description, options = {}) => {
    return show({
      title,
      description,
      ...options
    })
  }

  const confirmDelete = (itemName, options = {}) => {
    return show({
      title: 'Delete Item',
      description: `Are you sure you want to delete "${itemName}"? This action cannot be undone.`,
      iconType: 'danger',
      confirmVariant: 'danger',
      confirmText: 'Delete',
      ...options
    })
  }

  const confirmDeleteMultiple = (count, options = {}) => {
    return show({
      title: 'Delete Items',
      description: `Are you sure you want to delete ${count} item${count > 1 ? 's' : ''}? This action cannot be undone.`,
      iconType: 'danger',
      confirmVariant: 'danger',
      confirmText: 'Delete All',
      ...options
    })
  }

  const confirmSave = (options = {}) => {
    return show({
      title: 'Save Changes',
      description: 'Are you sure you want to save these changes?',
      iconType: 'info',
      confirmVariant: 'primary',
      confirmText: 'Save',
      ...options
    })
  }

  const confirmDiscard = (options = {}) => {
    return show({
      title: 'Discard Changes',
      description: 'Are you sure you want to discard your changes? This action cannot be undone.',
      iconType: 'warning',
      confirmVariant: 'danger',
      confirmText: 'Discard',
      ...options
    })
  }

  const confirmReset = (options = {}) => {
    return show({
      title: 'Reset Form',
      description: 'Are you sure you want to reset the form? All unsaved changes will be lost.',
      iconType: 'warning',
      confirmVariant: 'danger',
      confirmText: 'Reset',
      ...options
    })
  }

  const confirmLogout = (options = {}) => {
    return show({
      title: 'Logout',
      description: 'Are you sure you want to logout?',
      iconType: 'question',
      confirmVariant: 'primary',
      confirmText: 'Logout',
      ...options
    })
  }

  const confirmWithInput = (title, description, expectedInput, options = {}) => {
    return show({
      title,
      description,
      requireInput: true,
      expectedInput,
      inputLabel: `Type "${expectedInput}" to confirm`,
      inputPlaceholder: `Enter "${expectedInput}"`,
      ...options
    })
  }

  const confirmDangerousAction = (title, description, options = {}) => {
    return show({
      title,
      description,
      iconType: 'danger',
      confirmVariant: 'danger',
      warningText: 'This action is irreversible and may cause data loss.',
      ...options
    })
  }

  const handleConfirm = (data) => {
    if (confirmCallback) {
      confirmCallback(data)
    }
  }

  const handleCancel = () => {
    if (cancelCallback) {
      cancelCallback()
    }
  }

  const handleClose = () => {
    if (cancelCallback) {
      cancelCallback()
    }
  }

  const setLoading = (value) => {
    loading.value = value
  }

  const close = () => {
    isOpen.value = false
    loading.value = false
  }

  // Preset configurations
  const presets = {
    delete: (itemName) => confirmDelete(itemName),
    deleteMultiple: (count) => confirmDeleteMultiple(count),
    save: () => confirmSave(),
    discard: () => confirmDiscard(),
    reset: () => confirmReset(),
    logout: () => confirmLogout(),
    dangerous: (title, description) => confirmDangerousAction(title, description),
    withInput: (title, description, expectedInput) => confirmWithInput(title, description, expectedInput)
  }

  return {
    // State
    isOpen: readonly(isOpen),
    loading: readonly(loading),
    config: readonly(config),
    
    // Methods
    show,
    confirm,
    confirmDelete,
    confirmDeleteMultiple,
    confirmSave,
    confirmDiscard,
    confirmReset,
    confirmLogout,
    confirmWithInput,
    confirmDangerousAction,
    handleConfirm,
    handleCancel,
    handleClose,
    setLoading,
    close,
    
    // Presets
    presets
  }
}

// Global confirmation service
let globalConfirmation = null

export const useGlobalConfirmation = () => {
  if (!globalConfirmation) {
    globalConfirmation = useConfirmation()
  }
  return globalConfirmation
}

// Convenience functions for common confirmations
export const confirmDelete = (itemName, options = {}) => {
  const { confirmDelete } = useGlobalConfirmation()
  return confirmDelete(itemName, options)
}

export const confirmDeleteMultiple = (count, options = {}) => {
  const { confirmDeleteMultiple } = useGlobalConfirmation()
  return confirmDeleteMultiple(count, options)
}

export const confirmSave = (options = {}) => {
  const { confirmSave } = useGlobalConfirmation()
  return confirmSave(options)
}

export const confirmDiscard = (options = {}) => {
  const { confirmDiscard } = useGlobalConfirmation()
  return confirmDiscard(options)
}

export const confirmReset = (options = {}) => {
  const { confirmReset } = useGlobalConfirmation()
  return confirmReset(options)
}

export const confirmLogout = (options = {}) => {
  const { confirmLogout } = useGlobalConfirmation()
  return confirmLogout(options)
}

export const confirmWithInput = (title, description, expectedInput, options = {}) => {
  const { confirmWithInput } = useGlobalConfirmation()
  return confirmWithInput(title, description, expectedInput, options)
}

export const confirmDangerousAction = (title, description, options = {}) => {
  const { confirmDangerousAction } = useGlobalConfirmation()
  return confirmDangerousAction(title, description, options)
}
